/**
 * CustomFieldsConfigDrawer
 *
 * Admin do schema de campos personalizados (nível workspace): cria/renomeia/
 * exclui ABAS e cria/edita/exclui CAMPOS tipados (com opções p/ select/
 * multiselect, flag de aniversário e permissões do agente de IA).
 *
 * Espelha a configuração de formulário do Kommo. As definições aqui valem para
 * TODOS os contatos do workspace (e os 2 inboxes).
 *
 * Props: isOpen, onClose, colors, onChanged()
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Plus, Trash2, Loader2, Pencil, Check, Star, AlertTriangle } from 'lucide-react';
import customFieldsService, {
  FIELD_TYPE_LABELS, OPTION_FIELD_TYPES,
} from '../../services/global/customFieldsService';
import authService from '../../services/global/authService';

const TYPE_KEYS = Object.keys(FIELD_TYPE_LABELS);
const DATE_TYPES = ['date', 'datetime'];

const emptyForm = () => ({
  id: null, name: '', type: 'text', options: [''],
  is_birthday: false, agent_readable: true, agent_writable: false,
  required_pipelines: [], required_stages: [], api_only: false,
  is_source: false,
  is_first_contact: false,
  _prevRole: null,
});

// Papel reservado: marca QUAL campo guarda a origem do lead. Um por workspace
// (o backend tira o papel do anterior sozinho). Sem ele o Painel do Gestor nao
// tem como saber onde a conta grava a origem.
const SOURCE_ROLE = 'lead_source';
// Papel reservado (so campo de data): a plataforma grava a data em que o
// contato foi criado, sem sobrescrever (flag FIRST_CONTACT_DATE_ROLE_ENABLED).
const FIRST_CONTACT_ROLE = 'first_contact_date';
// Papeis que ESTA tela liga e desliga. Os demais (ad_campaign, ad_id) sao
// marcados pelo MCP/API e nao podem ser apagados so por editar o nome do campo.
const UI_ROLES = [SOURCE_ROLE, FIRST_CONTACT_ROLE];

const roleFromForm = (form) => {
  if (form.is_source && OPTION_FIELD_TYPES.includes(form.type)) return SOURCE_ROLE;
  if (form.is_first_contact && DATE_TYPES.includes(form.type)) return FIRST_CONTACT_ROLE;
  return null;
};

const CustomFieldsConfigDrawer = ({ isOpen, onClose, colors, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [definitions, setDefinitions] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [newTabName, setNewTabName] = useState('');
  const [form, setForm] = useState(emptyForm());
  // Funis + etiquetas (etapas) para o seletor "Obrigatório em".
  const [funnels, setFunnels] = useState([]);
  const [labels, setLabels] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, funnelList, labelRes] = await Promise.all([
        customFieldsService.getSchema(),
        authService.listFunnels().catch(() => []),
        authService.getLabels().catch(() => ({})),
      ]);
      const sortedTabs = (data.tabs || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setTabs(sortedTabs);
      setDefinitions(data.definitions || []);
      setActiveTab((prev) => prev || (sortedTabs[0] && sortedTabs[0].id) || null);
      setFunnels(Array.isArray(funnelList) ? funnelList : (funnelList?.data || []));
      const lbls = Array.isArray(labelRes) ? labelRes : (labelRes?.labels || labelRes?.data || []);
      setLabels(lbls);
    } catch (err) {
      console.error('[CustomFieldsConfig] load error:', err);
      setError('Não foi possível carregar a configuração.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) { load(); setForm(emptyForm()); } }, [isOpen, load]);

  if (!isOpen) return null;

  const notifyChanged = () => { onChanged?.(); };

  const fieldsOfTab = definitions
    .filter((d) => d.tab_id === activeTab)
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  // ---- Abas ----
  const handleCreateTab = async () => {
    const name = newTabName.trim();
    if (!name) return;
    setBusy(true); setError(null);
    try {
      const tab = await customFieldsService.createTab(name);
      setTabs((prev) => [...prev, tab]);
      setActiveTab(tab.id);
      setNewTabName('');
      notifyChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao criar aba.');
    } finally { setBusy(false); }
  };

  const handleRenameTab = async (tab) => {
    const name = window.prompt('Novo nome da aba:', tab.name);
    if (!name || name.trim() === tab.name) return;
    setBusy(true); setError(null);
    try {
      const updated = await customFieldsService.renameTab(tab.id, name.trim());
      setTabs((prev) => prev.map((t) => (t.id === tab.id ? { ...t, ...updated } : t)));
      notifyChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao renomear aba.');
    } finally { setBusy(false); }
  };

  const handleDeleteTab = async (tab) => {
    if (tab.is_default) return;
    if (!window.confirm(`Excluir a aba "${tab.name}"? Os campos dela migram para a aba padrão.`)) return;
    setBusy(true); setError(null);
    try {
      await customFieldsService.deleteTab(tab.id);
      await load();
      setActiveTab((prev) => (prev === tab.id ? null : prev));
      notifyChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao excluir aba.');
    } finally { setBusy(false); }
  };

  // ---- Campos ----
  const startEdit = (def) => {
    setForm({
      id: def.id,
      name: def.name,
      type: def.type,
      options: OPTION_FIELD_TYPES.includes(def.type)
        ? (def.options || []).map((o) => o.label)
        : [''],
      is_birthday: !!def.is_birthday,
      agent_readable: def.agent_readable !== false,
      agent_writable: !!def.agent_writable,
      required_pipelines: def.required_pipelines || [],
      required_stages: def.required_stages || [],
      api_only: !!def.api_only,
      is_source: def.role === SOURCE_ROLE,
      is_first_contact: def.role === FIRST_CONTACT_ROLE,
      _prevRole: def.role || null,
      _optionObjs: def.options || [],
    });
  };

  const handleSubmitField = async () => {
    const name = form.name.trim();
    if (!name) { setError('Informe o nome do campo.'); return; }
    const isOptionType = OPTION_FIELD_TYPES.includes(form.type);
    const cleanOptions = (form.options || []).map((s) => (s || '').trim()).filter(Boolean);
    if (isOptionType && cleanOptions.length === 0) {
      setError('Adicione ao menos uma opção.'); return;
    }
    setBusy(true); setError(null);
    try {
      if (form.id) {
        // Edição — preserva os ids das opções já existentes (pela posição/label).
        let optionsPayload;
        if (isOptionType) {
          const prev = form._optionObjs || [];
          optionsPayload = cleanOptions.map((label) => {
            const match = prev.find((o) => o.label === label);
            return match ? { id: match.id, label } : { label };
          });
        }
        const patch = {
          name,
          is_birthday: DATE_TYPES.includes(form.type) ? form.is_birthday : false,
          agent_readable: form.agent_readable,
          agent_writable: form.agent_writable,
          required_pipelines: form.required_pipelines,
          required_stages: form.required_stages,
          api_only: form.api_only,
        };
        // '' limpa o papel; o backend recusa papel nao atribuivel. So limpa
        // papel que esta tela controla: o de anuncio fica como estava.
        const nextRole = roleFromForm(form);
        if (nextRole) patch.role = nextRole;
        else if (UI_ROLES.includes(form._prevRole)) patch.role = '';
        if (isOptionType) patch.options = optionsPayload;
        const updated = await customFieldsService.updateDefinition(form.id, patch);
        setDefinitions((prev) => prev.map((d) => (d.id === form.id ? { ...d, ...updated } : d)));
      } else {
        const created = await customFieldsService.createDefinition({
          name,
          type: form.type,
          tab_id: activeTab,
          options: isOptionType ? cleanOptions : undefined,
          is_birthday: DATE_TYPES.includes(form.type) ? form.is_birthday : false,
          agent_readable: form.agent_readable,
          agent_writable: form.agent_writable,
          required_pipelines: form.required_pipelines,
          required_stages: form.required_stages,
          api_only: form.api_only,
          role: roleFromForm(form) || undefined,
        });
        setDefinitions((prev) => [...prev, created]);
      }
      setForm(emptyForm());
      notifyChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao salvar o campo.');
    } finally { setBusy(false); }
  };

  const handleDeleteField = async (def) => {
    if (!window.confirm(`Excluir o campo "${def.name}"? Os valores já preenchidos deixam de aparecer.`)) return;
    setBusy(true); setError(null);
    try {
      await customFieldsService.deleteDefinition(def.id);
      setDefinitions((prev) => prev.filter((d) => d.id !== def.id));
      if (form.id === def.id) setForm(emptyForm());
      notifyChanged();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Erro ao excluir campo.');
    } finally { setBusy(false); }
  };

  const isOptionType = OPTION_FIELD_TYPES.includes(form.type);
  const isDateType = DATE_TYPES.includes(form.type);

  const inputStyle = {
    width: '100%', padding: '8px 10px', fontSize: 13,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    border: `1px solid ${colors.borderColor}`, borderRadius: 8,
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1300, display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 520, maxWidth: '100%', height: '100%',
          backgroundColor: colors.bgPrimary,
          boxShadow: '-8px 0 32px rgba(0,0,0,0.25)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: `1px solid ${colors.borderColor}`,
          backgroundColor: colors.headerBg,
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.textPrimary }}>
            Configurar campos personalizados
          </h3>
          <button onClick={onClose} aria-label="Fechar" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: colors.textSecondary, padding: 6, borderRadius: 8,
          }}>
            <X size={20} />
          </button>
        </div>

        {loading ? (
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, color: colors.textSecondary, fontSize: 13,
          }}>
            <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Carregando…
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {/* Abas */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 8 }}>
                Abas do formulário
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {tabs.map((tab) => {
                  const active = tab.id === activeTab;
                  return (
                    <div key={tab.id} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '5px 8px 5px 12px', borderRadius: 8,
                      border: `1px solid ${active ? colors.accentPrimary : colors.borderColor}`,
                      backgroundColor: active ? `${colors.accentPrimary}14` : 'transparent',
                    }}>
                      <button onClick={() => setActiveTab(tab.id)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        fontSize: 12, fontWeight: active ? 700 : 600,
                        color: active ? colors.accentPrimary : colors.textSecondary,
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}>
                        {tab.is_default && <Star size={11} />} {tab.name}
                      </button>
                      <button onClick={() => handleRenameTab(tab)} title="Renomear" style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.textTertiary, padding: 2, display: 'inline-flex',
                      }}>
                        <Pencil size={11} />
                      </button>
                      {!tab.is_default && (
                        <button onClick={() => handleDeleteTab(tab)} title="Excluir aba" style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#DC2626', padding: 2, display: 'inline-flex',
                        }}>
                          <Trash2 size={11} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  value={newTabName}
                  onChange={(e) => setNewTabName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTab(); }}
                  placeholder="Nova aba (ex.: Veículo)"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button onClick={handleCreateTab} disabled={busy || !newTabName.trim()} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0 12px',
                  backgroundColor: colors.accentPrimary, color: '#fff', border: 'none',
                  borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  opacity: !newTabName.trim() ? 0.6 : 1,
                }}>
                  <Plus size={14} /> Aba
                </button>
              </div>
            </div>

            {/* Campos da aba ativa */}
            <div style={{
              borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14, marginBottom: 16,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 8 }}>
                Campos desta aba
              </div>
              {fieldsOfTab.length === 0 ? (
                <div style={{ fontSize: 12, fontStyle: 'italic', color: colors.textTertiary, padding: 6 }}>
                  Nenhum campo. Use o formulário abaixo para criar.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {fieldsOfTab.map((def) => (
                    <div key={def.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                      borderRadius: 8, backgroundColor: colors.bgSecondary,
                      border: `1px solid ${form.id === def.id ? colors.accentPrimary : colors.borderColor}`,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: colors.textPrimary, fontWeight: 500 }}>
                          {def.name}
                          {def.is_birthday && (
                            <span style={{ fontSize: 10, color: colors.accentPrimary, marginLeft: 6 }}>
                              aniversário
                            </span>
                          )}
                          {def.role === 'value' && (
                            <span style={{ fontSize: 10, color: colors.accentPrimary, marginLeft: 6 }}>
                              campo de valor
                            </span>
                          )}
                          {def.role === FIRST_CONTACT_ROLE && (
                            <span style={{ fontSize: 10, color: colors.accentPrimary, marginLeft: 6 }}>
                              data do primeiro contato
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: colors.textTertiary }}>
                          {FIELD_TYPE_LABELS[def.type] || def.type}
                          {def.agent_writable ? ' · agente edita' : def.agent_readable !== false ? ' · agente lê' : ' · oculto do agente'}
                        </div>
                      </div>
                      <button onClick={() => startEdit(def)} title="Editar" style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.textTertiary, padding: 4,
                      }}>
                        <Pencil size={13} />
                      </button>
                      {def.role !== 'value' && (
                        <button onClick={() => handleDeleteField(def)} title="Excluir" style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#DC2626', padding: 4,
                        }}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Formulário criar/editar campo */}
            <div style={{
              borderTop: `1px solid ${colors.borderColor}`, paddingTop: 14,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary }}>
                {form.id ? 'Editar campo' : 'Novo campo'}
              </div>
              <input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Nome do campo (ex.: Modelo)"
                style={inputStyle}
              />
              <select
                value={form.type}
                disabled={!!form.id}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                title={form.id ? 'O tipo não pode ser alterado após criado' : ''}
                style={{ ...inputStyle, opacity: form.id ? 0.6 : 1 }}
              >
                {TYPE_KEYS.map((t) => (
                  <option key={t} value={t}>{FIELD_TYPE_LABELS[t]}</option>
                ))}
              </select>

              {isOptionType && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ fontSize: 11, color: colors.textSecondary }}>Opções</span>
                  {(form.options || []).map((opt, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={opt}
                        onChange={(e) => setForm((f) => {
                          const next = [...f.options];
                          next[idx] = e.target.value;
                          return { ...f, options: next };
                        })}
                        placeholder={`Opção ${idx + 1}`}
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <button onClick={() => setForm((f) => ({
                        ...f, options: f.options.filter((_, i) => i !== idx),
                      }))} style={{
                        background: 'none', border: `1px solid ${colors.borderColor}`,
                        borderRadius: 8, cursor: 'pointer', color: '#DC2626', padding: '0 10px',
                      }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setForm((f) => ({ ...f, options: [...f.options, ''] }))} style={{
                    alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: colors.accentPrimary, fontSize: 12, fontWeight: 600, padding: 2,
                  }}>
                    <Plus size={13} /> Adicionar opção
                  </button>
                </div>
              )}

              {isDateType && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_birthday}
                    onChange={(e) => setForm((f) => ({ ...f, is_birthday: e.target.checked }))}
                    style={{ accentColor: colors.accentPrimary }} />
                  Usar para automação de aniversário
                </label>
              )}

              {isDateType && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_first_contact}
                    onChange={(e) => setForm((f) => ({ ...f, is_first_contact: e.target.checked }))}
                    style={{ accentColor: colors.accentPrimary }} />
                  Data do primeiro contato (preenchida pela plataforma quando o contato é criado)
                </label>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.agent_readable}
                  onChange={(e) => setForm((f) => ({ ...f, agent_readable: e.target.checked }))}
                  style={{ accentColor: colors.accentPrimary }} />
                Agente de IA pode LER este campo
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.agent_writable}
                  onChange={(e) => setForm((f) => ({ ...f, agent_writable: e.target.checked }))}
                  style={{ accentColor: colors.accentPrimary }} />
                Agente de IA pode PREENCHER este campo
              </label>

              {isOptionType && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.is_source}
                    onChange={(e) => setForm((f) => ({ ...f, is_source: e.target.checked }))}
                    style={{ accentColor: colors.accentPrimary }} />
                  Este campo guarda a ORIGEM do lead (usado no Painel do Gestor)
                </label>
              )}

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: colors.textPrimary, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.api_only}
                  onChange={(e) => setForm((f) => ({ ...f, api_only: e.target.checked }))}
                  style={{ accentColor: colors.accentPrimary }} />
                Apenas API (não aparece no formulário do contato)
              </label>

              {/* Obrigatório em funis/etapas — apenas SINALIZAÇÃO no ChatWindow */}
              <div style={{
                border: `1px solid ${colors.borderColor}`, borderRadius: 8, padding: 10,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
                  fontSize: 12, fontWeight: 600, color: colors.textSecondary,
                }}>
                  <AlertTriangle size={13} color="#D97706" /> Obrigatório em
                </div>
                <div style={{ fontSize: 11, color: colors.textTertiary, marginBottom: 8 }}>
                  Sinaliza no atendimento quando estes funis/etapas exigem este campo. Não bloqueia a mudança de etapa.
                </div>
                {funnels.length === 0 ? (
                  <div style={{ fontSize: 11, fontStyle: 'italic', color: colors.textTertiary }}>
                    Nenhum funil configurado ainda.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 200, overflowY: 'auto' }}>
                    {funnels.map((fn) => {
                      const wholeFunnel = form.required_pipelines.includes(fn.id);
                      const stages = labels
                        .filter((l) => l.pipeline_id === fn.id)
                        .sort((a, b) => (a.order || 0) - (b.order || 0));
                      const toggleFunnel = () => setForm((f) => ({
                        ...f,
                        required_pipelines: wholeFunnel
                          ? f.required_pipelines.filter((id) => id !== fn.id)
                          : [...f.required_pipelines, fn.id],
                      }));
                      const toggleStage = (lid) => setForm((f) => ({
                        ...f,
                        required_stages: f.required_stages.includes(lid)
                          ? f.required_stages.filter((id) => id !== lid)
                          : [...f.required_stages, lid],
                      }));
                      return (
                        <div key={fn.id}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textPrimary, cursor: 'pointer', fontWeight: 600 }}>
                            <input type="checkbox" checked={wholeFunnel} onChange={toggleFunnel}
                              style={{ accentColor: colors.accentPrimary }} />
                            {fn.name} <span style={{ fontSize: 10, color: colors.textTertiary, fontWeight: 400 }}>(funil inteiro)</span>
                          </label>
                          {!wholeFunnel && stages.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '4px 0 0 22px' }}>
                              {stages.map((l) => {
                                const on = form.required_stages.includes(l.id);
                                return (
                                  <button key={l.id} onClick={() => toggleStage(l.id)} style={{
                                    padding: '3px 8px', fontSize: 11, borderRadius: 12, cursor: 'pointer',
                                    border: `1px solid ${on ? '#D97706' : colors.borderColor}`,
                                    backgroundColor: on ? '#D9770618' : 'transparent',
                                    color: on ? '#B45309' : colors.textSecondary, fontWeight: on ? 600 : 500,
                                  }}>
                                    {l.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {error && (
                <div style={{ fontSize: 11, color: '#DC2626' }}>{error}</div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                {form.id && (
                  <button onClick={() => { setForm(emptyForm()); setError(null); }} style={{
                    padding: '8px 14px', background: 'transparent', color: colors.textSecondary,
                    border: `1px solid ${colors.borderColor}`, borderRadius: 8, cursor: 'pointer', fontSize: 13,
                  }}>
                    Cancelar
                  </button>
                )}
                <button onClick={handleSubmitField} disabled={busy || !form.name.trim() || !activeTab} style={{
                  flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  padding: '8px 14px', backgroundColor: colors.accentPrimary, color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  opacity: (!form.name.trim() || !activeTab) ? 0.6 : 1,
                }}>
                  {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />}
                  {form.id ? 'Salvar alterações' : 'Adicionar campo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomFieldsConfigDrawer;
